import React, { useState, useEffect, useRef } from 'react';
import { MagnifyingGlass, Command, KeyReturn, ArrowsDownUp } from '@phosphor-icons/react';
import { get_icon } from '../utils/icon_map';
import { MmcBadge } from './ui';
import '../styles/mmc-global-search.css';

const MmcGlobalSearch = ({ modules, onSelect }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const inputRef = useRef(null);

    const filteredModules = modules.filter(m =>
        m.display_name.toLowerCase().includes(query.toLowerCase()) ||
        m.description.toLowerCase().includes(query.toLowerCase())
    );

    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setIsOpen(true);
            }
            if (e.key === 'Escape') {
                setIsOpen(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
        if (isOpen) {
            inputRef.current?.focus();
            setQuery('');
            setActiveIndex(0);
        }
    }, [isOpen]);

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex(prev => (prev + 1) % filteredModules.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex(prev => (prev - 1 + filteredModules.length) % filteredModules.length);
        } else if (e.key === 'Enter') {
            if (filteredModules[activeIndex]) {
                selectModule(filteredModules[activeIndex].id);
            }
        }
    };

    const selectModule = (moduleId) => {
        onSelect(moduleId);
        setIsOpen(false);
    };

    if (!isOpen) return null;

    return (
        <div className="mmc-global-search-overlay" onClick={() => setIsOpen(false)}>
            <div className="mmc-global-search" onClick={e => e.stopPropagation()}>
                <header className="mmc-global-search__header">
                    <MagnifyingGlass size={24} weight="bold" />
                    <input
                        ref={inputRef}
                        type="text"
                        className="mmc-global-search__input"
                        placeholder="Search for a tool..."
                        value={query}
                        onChange={e => {
                            setQuery(e.target.value);
                            setActiveIndex(0);
                        }}
                        onKeyDown={handleKeyDown}
                    />
                    <div className="mmc-global-search__kbd">
                        <MmcBadge><Command size={10} /></MmcBadge>
                        <MmcBadge>K</MmcBadge>
                    </div>
                </header>

                <div className="mmc-global-search__results">
                    {filteredModules.length > 0 ? (
                        filteredModules.map((m, index) => (
                            <div
                                key={m.id}
                                className={`mmc-global-search__item ${index === activeIndex ? 'mmc-global-search__item--active' : ''}`}
                                onMouseEnter={() => setActiveIndex(index)}
                                onClick={() => selectModule(m.id)}
                            >
                                {get_icon(m.icon, 20, 'bold')}
                                <div className="mmc-global-search__item-info">
                                    <span className="mmc-global-search__item-title">{m.display_name}</span>
                                    <span className="mmc-global-search__item-desc">{m.description}</span>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="mmc-global-search__empty">
                            No tools found for "{query}"
                        </div>
                    )}
                </div>

                <footer className="mmc-global-search__footer">
                    <span className="mmc-global-search__hint">
                        <MmcBadge><KeyReturn size={10} /></MmcBadge> to select
                    </span>
                    <span className="mmc-global-search__hint">
                        <MmcBadge><ArrowsDownUp size={10} /></MmcBadge> to navigate
                    </span>
                    <span className="mmc-global-search__hint">
                        <MmcBadge style={{ padding: '0 4px' }}>ESC</MmcBadge> to close
                    </span>
                </footer>
            </div>
        </div>
    );
};

export default MmcGlobalSearch;
